import { FC, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Checkbox, Radio } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwEditingStore, useIwExecutionStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import {
  useIwRecordingStore,
  WitnessRecordings,
} from '@/entities/interwills/stores/iwRecording.store';
import { Alert } from '@/shared/components/Alert';
import { COLORS } from '@/shared/constants/colors';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddVideoVerificationModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';

enum VerificationType {
  TOGETHER = 'TOGETHER',
  ALONE = 'ALONE',
}

export const AddVideoVerificationModal: FC<AddVideoVerificationModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addVideoVerification' });

  const modals = useModalsStore();
  const { witnesses } = useIwEditingStore();
  const { witnessVerifications } = useIwExecutionStore();
  const recording = useIwRecordingStore();
  const router = useRouter();

  const [verificationType, setVerificationType] = useState<VerificationType>();
  const [selectedWitnesses, setSelectedWitnesses] = useState<DtoId[]>();
  const [showError, setShowError] = useState(false);

  const onSubmit = () => {
    if (!verificationType) return;

    if (verificationType === VerificationType.TOGETHER && !selectedWitnesses?.length) {
      setShowError(true);
      return;
    }
    let wintessMap: WitnessRecordings = {};
    if (verificationType === VerificationType.TOGETHER && selectedWitnesses) {
      wintessMap = selectedWitnesses.reduce((acc, curr) => {
        return {
          ...acc,
          [curr]: null,
        };
      }, {});
    }

    recording.setTestator(null);
    recording.setWintesses(wintessMap);
    recording.setIsStarted(true);

    router.push('/interwill/verification');
    modals.closeModal();
  };

  const filteredWitnesses = useMemo(() => {
    const existedVerification = new Set(
      Object.keys(witnessVerifications).filter((key) => witnessVerifications[key] !== null),
    );

    return witnesses
      .map((item, i) => {
        if (item.id && !existedVerification.has(item.id)) {
          return {
            witness: item,
            index: i,
          };
        }
        return null;
      })
      .filter((item) => item !== null);
  }, [witnesses]);

  return (
    <ModalContainerV1 title={t('title')}>
      <InnerForm>
        <Radio.Group
          onChange={(e) => {
            setShowError(false);
            setVerificationType(e.target.value);
          }}
        >
          <RadioStyled value={VerificationType.TOGETHER}>
            <RadioContent>
              <b>{t('together')}</b>
              <span> {t('togetherDescription')}</span>
            </RadioContent>
          </RadioStyled>
          {verificationType === VerificationType.TOGETHER && (
            <Witnesses>
              <b>{t('selectWitness')}</b>
              <CheckboxGroupStyled
                onChange={(data) => {
                  setSelectedWitnesses([...data]);
                }}
              >
                {filteredWitnesses.map((item) => (
                  <Checkbox value={item.witness.id} key={item.index}>
                    {getBeneficiaryFullName({
                      firstName: item.witness.firstName ?? '',
                      lastName: item.witness.lastName ?? '',
                    })}
                    , {t('witness')} {item.index + 1}
                  </Checkbox>
                ))}
              </CheckboxGroupStyled>
              {showError && !selectedWitnesses?.length && <Alert text={t('error')} color="red" />}
            </Witnesses>
          )}
          <Separator />
          <RadioStyled value={VerificationType.ALONE}>
            <RadioContent>
              <b>{t('alone')}</b>
              <span> {t('aloneDescription')}</span>
            </RadioContent>
          </RadioStyled>
        </Radio.Group>
      </InnerForm>
      <Buttons>
        <ButtonIw onClick={modals.closeModal} type="default">
          {t('cancel')}
        </ButtonIw>
        <ButtonIw onClick={onSubmit}>{t('next')}</ButtonIw>
      </Buttons>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 24px;
  min-height: 240px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  gap: 16px;
`;

const Separator = styled.div`
  margin: 24px 0;
  width: 100%;
  height: 1px;
  background: ${COLORS.systemDark100};
`;

const RadioStyled = styled(Radio)`
  &&& {
    display: flex;
    gap: 8px;
    align-items: flex-start;

    .ant-radio {
      padding-top: 4px;
      align-self: flex-start;
    }
  }
`;

const RadioContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const Witnesses = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  margin-top: 16px;
  padding-left: 28px;
`;

const CheckboxGroupStyled = styled(Checkbox.Group<DtoId>)`
  flex-direction: column;
  gap: 12px;
`;
